import { useUpdateIntegration } from '@modules/integrationsV2/hooks';
import { EditIntegrationDrawerContentProps } from '@modules/integrationsV2/types';
import { trackEditIntegrationEvent } from '@modules/integrationsV2/utils';
import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Checkbox,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { IntegrationChannelType, Survey, SurveyInput } from 'generated/graphql';
import { useEffect, useMemo, useState } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { AndOfErrors } from 'v2/core/errors/errors';
import useGetIntegrationEditFormData from 'v2/core/repository/edge-server/integrations/editIntegrations/useGetIntegrationEditFormData';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { AddOrEditIntegrationDrawerHeader } from '../../AddOrEditIntegrationDrawerHeader';

export const EditSurveyMonkeyIntegrationDrawerContent = ({
  onDrawerClose,
  integrationID,
  integrationData,
  integrationType,
  integrationChannel,
}: EditIntegrationDrawerContentProps) => {
  const {
    data,
    status: fStatus,
    error: fError,
  } = useGetIntegrationEditFormData({ integrationID });

  const [updateIntegration, { status: uStatus, error: uError }] =
    useUpdateIntegration({
      channelType: IntegrationChannelType.SurveyMonkey,
      integrationType,
      onCompleted: () => {
        onDrawerClose();
        trackEditIntegrationEvent({
          eventName: 'Edit Integration Submitted',
          channelType: IntegrationChannelType.SurveyMonkey,
          integrationID,
        });
      },
    });

  const allSurveys = useMemo(
    () =>
      data?.surveyMonkey?.surveys.filter((survey): survey is Survey =>
        Boolean(survey),
      ) ?? [],
    [data?.surveyMonkey?.surveys],
  );

  const savedSurveys = useMemo(
    () => integrationData.surveyMonkey?.includedSurveys ?? [],
    [integrationData.surveyMonkey?.includedSurveys],
  );

  const [selectedSurveys, setSelectedSurveys] =
    useState<SurveyInput[]>(savedSurveys);

  useEffect(() => {
    if (savedSurveys.length > 0) {
      setSelectedSurveys(savedSurveys);
    }
  }, [savedSurveys]);

  const handleSubmit = () => {
    updateIntegration({
      integrationID,
      isActive: true,
      updateData: {
        surveyMonkey: {
          includedSurveys: selectedSurveys,
        },
      },
    });
  };

  const isIntegrationDataLoading = IsLoading(fStatus);

  const error = AndOfErrors([fError, uError]);
  useErrorHandler(error);

  return (
    <>
      <AddOrEditIntegrationDrawerHeader
        integrationChannel={integrationChannel}
        integrationType={integrationType}
        title="Edit SurveyMonkey Integration"
      />
      <Stack p={3} spacing={3}>
        <Autocomplete
          fullWidth
          multiple
          limitTags={10}
          disableCloseOnSelect
          options={allSurveys}
          value={selectedSurveys}
          getOptionLabel={(option) => option.name}
          onChange={(_, data) => setSelectedSurveys(data)}
          renderInput={(params) => (
            <TextField {...params} placeholder="Select forms" />
          )}
          renderOption={(props, option, { selected }) => {
            return (
              <Box {...props} component="li" style={{ padding: '2px' }}>
                <Stack alignItems="center" direction="row">
                  <Checkbox size="small" checked={selected} />
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body1">{option.name}</Typography>
                  </Stack>
                </Stack>
              </Box>
            );
          }}
          isOptionEqualToValue={(option, value) =>
            option.surveyID === value.surveyID
          }
          componentsProps={{ paper: { elevation: 8 } }}
          loading={isIntegrationDataLoading}
        />
        <LoadingButton
          fullWidth
          variant="contained"
          size="large"
          onClick={handleSubmit}
          loading={IsLoading(uStatus)}
        >
          Save Changes
        </LoadingButton>
      </Stack>
    </>
  );
};
